import { useCallback, useEffect, useState } from 'react';

/**
 * The website pages' two small per-browser preferences: the theme the header's
 * Theme menu sets, and where to go back to after logging in.
 *
 * Both are conveniences for one viewer, kept in browser storage and read
 * defensively — a private window or blocked storage must not break a page.
 */

// -- Theme -------------------------------------------------------------------

export type ThemeChoice = 'light' | 'dark' | 'system';

const THEME_KEY = 'jt-theme';

export function readTheme(): ThemeChoice {
  try {
    const stored = window.localStorage.getItem(THEME_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    return 'system';
  }
}

function prefersDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Writes `data-theme` on <html>, which the site stylesheet turns into the frame's dark rendering. */
function applyTheme(choice: ThemeChoice): void {
  const dark = choice === 'dark' || (choice === 'system' && prefersDark());
  document.documentElement.dataset.theme = dark ? 'dark' : 'light';
}

/**
 * The theme, applied while the calling page is mounted and removed when it
 * leaves.
 *
 * Removed, not left behind: the homepage has no theme control and is frozen, so
 * navigating from a website page back to Home must not carry a dark rendering
 * onto it.
 */
export function useTheme(): readonly [ThemeChoice, (choice: ThemeChoice) => void] {
  const [choice, setChoice] = useState<ThemeChoice>(() => readTheme());

  useEffect(() => {
    applyTheme(choice);
    if (choice !== 'system' || typeof window.matchMedia !== 'function') return undefined;

    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const follow = (): void => applyTheme('system');
    query.addEventListener?.('change', follow);
    return () => query.removeEventListener?.('change', follow);
  }, [choice]);

  useEffect(
    () => () => {
      delete document.documentElement.dataset.theme;
    },
    [],
  );

  const choose = useCallback((next: ThemeChoice) => {
    try {
      window.localStorage.setItem(THEME_KEY, next);
    } catch {
      // Not remembered, still applied.
    }
    setChoice(next);
  }, []);

  return [choice, choose] as const;
}

// -- Where to go after logging in ---------------------------------------------

const AFTER_LOGIN_KEY = 'jt-after-login';

/**
 * Only a path on this site. A full URL, a protocol-relative `//host`, or
 * anything with a backslash is refused, so the return path cannot be turned
 * into an open redirect.
 */
export function safeReturnPath(value: string | null | undefined): string | undefined {
  if (value === null || value === undefined) return undefined;
  if (!value.startsWith('/') || value.startsWith('//') || value.includes('\\')) return undefined;
  if (value.startsWith('/login')) return undefined;
  return value;
}

/** Remembers the page someone was on when they chose Log in or Create an account. */
export function rememberReturnPath(path: string = window.location.pathname + window.location.search): void {
  const safe = safeReturnPath(path);
  if (safe === undefined) return;
  try {
    window.sessionStorage.setItem(AFTER_LOGIN_KEY, safe);
  } catch {
    // Without storage the person lands on the default page instead.
  }
}

/** The remembered page, read once and forgotten. */
export function takeReturnPath(): string | undefined {
  try {
    const value = safeReturnPath(window.sessionStorage.getItem(AFTER_LOGIN_KEY));
    window.sessionStorage.removeItem(AFTER_LOGIN_KEY);
    return value;
  } catch {
    return undefined;
  }
}

/** The remembered page, without forgetting it. */
export function peekReturnPath(): string | undefined {
  try {
    return safeReturnPath(window.sessionStorage.getItem(AFTER_LOGIN_KEY));
  } catch {
    return undefined;
  }
}
